import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Shield, Calculator, TrendingUp, TrendingDown, 
  RefreshCcw, AlertTriangle, Percent,
  CheckCircle, Info, Zap, Users, Building, Target
} from 'lucide-react';
import { formatAED } from '../utils/currency';
import AEDIcon from './icons/AEDIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Risk-Adjusted Commitment Admin Panel
 * Phase 1: Shadow Mode - Admin Only
 * 
 * Dynamic commitment calculation based on BSS/ARS risk profiles
 */
const CommitmentAdminPanel = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Simulation inputs
  const [simPrice, setSimPrice] = useState(5000000);
  const [simBssTier, setSimBssTier] = useState('SERIOUS');
  const [simArsTier, setSimArsTier] = useState('');
  const [simTxType, setSimTxType] = useState('SECONDARY');

  useEffect(() => {
    fetchOverview();
  }, [token]);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/commitment/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setOverview(data.data);
      }
    } catch (err) {
      setError('Error connecting to Commitment Engine');
    }
    setLoading(false);
  };

  const runSimulation = async () => {
    try {
      const params = new URLSearchParams({
        purchase_price: simPrice,
        bss_tier: simBssTier,
        transaction_type: simTxType
      });
      if (simArsTier) params.append('ars_tier', simArsTier);
      
      const res = await fetch(`${API_URL}/api/admin/commitment/simulate?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSimulation(data.data);
      }
    } catch (err) {
      console.error('Simulation error:', err);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'REDUCED': return 'text-green-400 bg-green-600/20 border-green-600/30';
      case 'STANDARD': return 'text-blue-400 bg-blue-600/20 border-blue-600/30';
      case 'ELEVATED': return 'text-yellow-400 bg-yellow-600/20 border-yellow-600/30';
      case 'MAXIMUM': return 'text-red-400 bg-red-600/20 border-red-600/30';
      default: return 'text-gray-400 bg-gray-600/20 border-gray-600/30';
    }
  };

  const getBssTierColor = (tier) => {
    switch (tier) {
      case 'VERIFIED_PRIME': return 'text-emerald-400';
      case 'SERIOUS': return 'text-blue-400';
      case 'EXPLORATORY': return 'text-yellow-400';
      case 'NON_PRIORITY': return 'text-orange-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-8 text-center">
          <Shield className="w-8 h-8 animate-pulse mx-auto mb-4 text-amber-400" />
          <p className="text-muted-foreground">Loading Commitment Engine...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass-card border-red-600/30">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-400" />
          <p className="text-red-400">{error}</p>
          <Button onClick={fetchOverview} className="mt-4">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="commitment-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display text-amber-400 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Risk-Adjusted Commitment
          </h2>
          <p className="text-sm text-muted-foreground">Phase 1: Shadow Mode - Dynamic Commitment Based on Risk Profiles</p>
        </div>
        <Button variant="outline" onClick={fetchOverview} className="gap-2">
          <RefreshCcw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Shadow Mode Banner */}
      <Card className="glass-card border-amber-600/30 bg-amber-600/5">
        <CardContent className="p-4 flex items-center gap-4">
          <Zap className="w-5 h-5 text-amber-400" />
          <div>
            <p className="text-amber-400 font-medium">Commitment Engine Active (Shadow Mode)</p>
            <p className="text-xs text-muted-foreground">
              Calculates risk-adjusted commitment percentages using BSS and ARS scores. Higher trust = lower commitment required.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {['overview', 'simulate', 'config'].map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab)}
            className="capitalize"
            data-testid={`commitment-tab-${tab}`}
          >
            {tab === 'config' ? 'Configuration' : tab}
          </Button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="glass-card border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-amber-400">{overview.total_deals_analyzed}</div>
                <p className="text-xs text-muted-foreground">Deals Analyzed</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-green-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-green-400">{overview.average_commitment_rate_display}</div>
                <p className="text-xs text-muted-foreground">Avg Commitment Rate</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-primary/30">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-display text-primary">{overview.total_commitment_value_formatted}</div>
                <p className="text-xs text-muted-foreground">Total Committed</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-emerald-600/30">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-display text-emerald-400">{overview.tier_distribution?.REDUCED || 0}</div>
                <p className="text-xs text-muted-foreground">Reduced Tier (5-7%)</p>
              </CardContent>
            </Card>
          </div>

          {/* Tier Distribution */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-amber-400" />
                Commitment Tier Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {['REDUCED', 'STANDARD', 'ELEVATED', 'MAXIMUM'].map((tier) => (
                  <div key={tier} className={`p-4 rounded border ${getTierColor(tier)} text-center`}>
                    <div className="text-2xl font-display">{overview.tier_distribution?.[tier] || 0}</div>
                    <p className="text-xs mt-1">{tier}</p>
                    <p className="text-xs opacity-70">
                      {tier === 'REDUCED' && '5-7%'}
                      {tier === 'STANDARD' && '8-10%'}
                      {tier === 'ELEVATED' && '11-13%'}
                      {tier === 'MAXIMUM' && '14-20%'}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Calculations */}
          {overview.recent_calculations?.length > 0 && (
            <Card className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-amber-400" />
                  Recent Calculations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overview.recent_calculations.map((calc, idx) => (
                    <div key={idx} className="p-3 rounded bg-secondary/10 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{calc.deal_id?.slice(0, 8)}...</p>
                        <p className="text-xs text-muted-foreground">BSS: <span className={getBssTierColor(calc.bss_tier)}>{calc.bss_tier}</span></p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-display ${getTierColor(calc.tier).split(' ')[0]}`}>
                          {(calc.commitment_rate * 100).toFixed(1)}%
                        </p>
                        <p className="text-xs text-muted-foreground">{formatAED(calc.commitment_amount)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Simulate Tab */}
      {activeTab === 'simulate' && (
        <div className="grid grid-cols-2 gap-6">
          {/* Simulation Inputs */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-amber-400" />
                Simulation Parameters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Purchase Price (AED)</Label>
                <Input
                  type="number"
                  value={simPrice}
                  onChange={(e) => setSimPrice(Number(e.target.value))}
                  className="bg-secondary/20 border-white/10"
                  data-testid="sim-price-input"
                />
              </div>
              
              <div>
                <Label>BSS Tier</Label>
                <select
                  value={simBssTier}
                  onChange={(e) => setSimBssTier(e.target.value)}
                  className="w-full p-2 bg-secondary/20 border border-white/10 rounded text-foreground"
                  data-testid="sim-bss-select"
                >
                  <option value="VERIFIED_PRIME">Verified Prime (5-7%)</option>
                  <option value="SERIOUS">Serious (8-10%)</option>
                  <option value="EXPLORATORY">Exploratory (10-12%)</option>
                  <option value="NON_PRIORITY">Non-Priority (12-15%)</option>
                  <option value="UNSCORED">Unscored (10%)</option>
                </select>
              </div>
              
              <div>
                <Label>ARS Tier (if agent involved)</Label>
                <select
                  value={simArsTier}
                  onChange={(e) => setSimArsTier(e.target.value)}
                  className="w-full p-2 bg-secondary/20 border border-white/10 rounded text-foreground"
                  data-testid="sim-ars-select"
                >
                  <option value="">No Agent</option>
                  <option value="TIER_A">Tier A (No adjustment)</option>
                  <option value="TIER_B">Tier B (No adjustment)</option>
                  <option value="TIER_C">Tier C (+2%)</option>
                  <option value="RESTRICTED">Restricted (+5%)</option>
                </select>
              </div>
              
              <div>
                <Label>Transaction Type</Label>
                <select
                  value={simTxType}
                  onChange={(e) => setSimTxType(e.target.value)}
                  className="w-full p-2 bg-secondary/20 border border-white/10 rounded text-foreground"
                  data-testid="sim-type-select"
                >
                  <option value="SECONDARY">Secondary Market</option>
                  <option value="OFFPLAN_PRIMARY">Off-Plan Primary</option>
                  <option value="OFFPLAN_ASSIGNMENT">Off-Plan Assignment</option>
                </select>
              </div>
              
              <Button onClick={runSimulation} className="w-full gap-2" data-testid="run-simulation-btn">
                <Zap className="w-4 h-4" /> Run Simulation
              </Button>
            </CardContent>
          </Card>

          {/* Simulation Results */}
          <Card className="glass-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-400" />
                Simulation Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              {simulation ? (
                <div className="space-y-4">
                  {/* Main Result */}
                  <div className="text-center p-4 rounded-lg bg-secondary/20">
                    <div className={`text-4xl font-display ${getTierColor(simulation.result?.commitment_tier).split(' ')[0]}`}>
                      {simulation.result?.commitment_rate_display}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Commitment Rate</p>
                    <Badge className={`mt-2 ${getTierColor(simulation.result?.commitment_tier)}`}>
                      {simulation.result?.commitment_tier} Tier
                    </Badge>
                  </div>
                  
                  {/* Commitment Amount */}
                  <div className="p-3 rounded bg-amber-600/10 text-center">
                    <p className="text-2xl font-display text-amber-400">
                      {simulation.result?.commitment_amount_formatted}
                    </p>
                    <p className="text-xs text-muted-foreground">Required Commitment</p>
                  </div>
                  
                  {/* Tier Comparison */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Comparison by BSS Tier</p>
                    {simulation.tier_comparison && Object.entries(simulation.tier_comparison).map(([tier, data]) => (
                      <div key={tier} className="flex items-center justify-between p-2 rounded bg-secondary/10">
                        <span className={`text-xs ${getBssTierColor(tier)}`}>{tier.replace('_', ' ')}</span>
                        <span className="text-sm font-display">{data.rate_display}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Insight */}
                  {simulation.insight && (
                    <div className="p-3 rounded bg-primary/10 border border-primary/20">
                      <p className="text-xs text-muted-foreground flex items-start gap-2">
                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-primary" />
                        {simulation.insight}
                      </p>
                    </div>
                  )}
                  
                  {/* Milestone Schedule for Off-Plan */}
                  {simulation.milestone_schedule && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Milestone Schedule</p>
                      {simulation.milestone_schedule.map((m, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 rounded bg-secondary/10 text-xs">
                          <span>{m.description}</span>
                          <span className="font-display">{m.amount_formatted}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Calculator className="w-8 h-8 mx-auto mb-4 opacity-50" />
                  <p>Configure parameters and run simulation</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Config Tab */}
      {activeTab === 'config' && (
        <Card className="glass-card border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-amber-400" />
              Commitment Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Base Configuration */}
            <div className="p-4 rounded bg-secondary/10">
              <h3 className="text-sm font-medium mb-3">Base Configuration</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-display text-amber-400">10%</p>
                  <p className="text-xs text-muted-foreground">Base Rate (Secondary)</p>
                </div>
                <div>
                  <p className="text-2xl font-display text-green-400">5%</p>
                  <p className="text-xs text-muted-foreground">Minimum Rate</p>
                </div>
                <div>
                  <p className="text-2xl font-display text-red-400">20%</p>
                  <p className="text-xs text-muted-foreground">Maximum Rate</p>
                </div>
              </div>
            </div>
            
            {/* BSS Adjustments */}
            <div className="p-4 rounded bg-secondary/10">
              <h3 className="text-sm font-medium mb-3">BSS-Adjusted Commitment Ranges</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 rounded bg-emerald-600/10">
                  <span className="text-emerald-400 text-sm">VERIFIED_PRIME</span>
                  <span className="font-display">5-7% (0.6x multiplier)</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-blue-600/10">
                  <span className="text-blue-400 text-sm">SERIOUS</span>
                  <span className="font-display">8-10% (0.85x multiplier)</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-yellow-600/10">
                  <span className="text-yellow-400 text-sm">EXPLORATORY</span>
                  <span className="font-display">10-12% (1.1x multiplier)</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-orange-600/10">
                  <span className="text-orange-400 text-sm">NON_PRIORITY</span>
                  <span className="font-display">12-15% (1.3x multiplier)</span>
                </div>
              </div>
            </div>
            
            {/* ARS Adjustments */}
            <div className="p-4 rounded bg-secondary/10">
              <h3 className="text-sm font-medium mb-3">ARS Additional Modifiers</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 rounded bg-green-600/10">
                  <span className="text-green-400 text-sm">TIER_A / TIER_B</span>
                  <span className="font-display">No additional requirement</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-yellow-600/10">
                  <span className="text-yellow-400 text-sm">TIER_C</span>
                  <span className="font-display">+2% additional</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-red-600/10">
                  <span className="text-red-400 text-sm">RESTRICTED</span>
                  <span className="font-display">+5% additional</span>
                </div>
              </div>
            </div>
            
            <div className="text-xs text-muted-foreground text-center border-t border-white/10 pt-4">
              Configuration is read-only in Shadow Mode. Contact system administrator for changes.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CommitmentAdminPanel;
